import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/common.css';
import '../../styles/animations.css';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toast';
import {
  AnimatedButton,
  AnimatedSocialButton,
  PasswordStrengthIndicator
} from '../animations/AnimatedFormElements';
import useTokenValidation from '../../hooks/useTokenValidation';
import AuthPageShell from './AuthPageShell';
import ResendVerificationEmail from './ResendVerificationEmail';
import { savePendingVerificationEmail } from '../../utils/verificationEmailUtils';
import { getEnvUrl } from '../../utils/envUrl';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const Register = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Extract wizard context from query params
  const returnTo = searchParams.get('return_to');
  const draftSessionId = searchParams.get('draft_session_id');
  const nextStep = searchParams.get('next_step');
  
  // IMPORTANT: If embedded mode, always treat as wizard flow
  const isWizardFlow = embedded || returnTo === 'wizard';
  
  // Token validation hook - always call unconditionally (React Hooks rule)
  // In embedded mode or open registration, token is not required
  const tokenValidation = useTokenValidation({ requireToken: !embedded });
  const { isValidating, tokenData, error, isTokenValid } = embedded ? {} : tokenValidation;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });

  // Pre-populate email from validated token
  useEffect(() => {
    if (tokenData?.email) {
      setFormData(prev => ({
        ...prev,
        email: tokenData.email
      }));
    }
  }, [tokenData]);

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    if (name === 'password') {
      let strength = 0;
      if (value.length >= 8) strength += 1;
      if (/[A-Z]/.test(value)) strength += 1;
      if (/[a-z]/.test(value)) strength += 1;
      if (/[0-9]/.test(value)) strength += 1;
      if (/[^A-Za-z0-9]/.test(value)) strength += 1;
      setPasswordStrength(strength);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must be 10 digits';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and a number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/users/signup`, {
        username: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        mobile_number: formData.mobileNumber,
        password: formData.password,
        env_url: getEnvUrl(),
        // Include the invitation token for server-side validation
        invitation_token: tokenData?.token
      });

      if (response.data === true) {
        savePendingVerificationEmail(formData.email);
        setIsRegistrationComplete(true);
        toast.success('Registration successful! Please check your email to verify your account.');
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.log(error.response?.data);
      toast.error(error.response?.data?.detail?.message || 'An error occurred during registration. Please try again.');
      
      // setApiError(
      //   error.response?.data?.detail?.message || 'An error occurred during registration. Please try again.'
      // );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTermsClick = () => {
    console.log('Terms of Service clicked');
  };

  const handlePrivacyClick = () => {
    console.log('Privacy Policy clicked');
  };

  // Skip validation in embedded mode
  if (!embedded && isValidating) {
    return (
      <AuthPageShell title="Validating Invitation" subtitle="Please wait while we verify your invitation token..." bodyStyle={{ textAlign: 'center' }}>
        <div className="loading-container">
          <div className="auth-spinner" aria-hidden />
          <p className="auth-muted">Verifying invitation…</p>
        </div>
      </AuthPageShell>
    );
  }

  // Error state - user will be redirected, but show friendly message (skip in embedded)
  if (!embedded && (error || !isTokenValid)) {
    return (
      <AuthPageShell title="Invalid Invitation" subtitle={error || 'Your invitation token is invalid or has expired.'} bodyStyle={{ textAlign: 'center' }}>
        <div className="auth-status-icon auth-status-icon--error" aria-hidden>!</div>
        <p className="auth-muted">Redirecting you to the login page...</p>
      </AuthPageShell>
    );
  }

  // Embedded mode - return just the registration form
  if (embedded) {
    if (isRegistrationComplete) {
      return (
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📧</div>
          <h3 style={{ marginBottom: '8px' }}>Check Your Email</h3>
          <p className="auth-muted" style={{ marginBottom: '16px' }}>
            We sent a verification link to <strong>{formData.email}</strong>.
            Please click the link in the email to activate your account before signing in.
          </p>
          <ResendVerificationEmail
            defaultEmail={formData.email}
            showEmailInput={false}
            readOnlyEmail
            variant="link"
            className="mb-4"
          />
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => navigate('/get-started/auth?tab=signin')}
          >
            Go to Sign In
          </button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit}>
        {apiError && <div className="form-error mb-3">{apiError}</div>}

        {/* First Name & Last Name */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">First Name</label>
            <input type="text" id="firstName" name="firstName"
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              placeholder="John"
              value={formData.firstName}
              onChange={handleChange}
              autoComplete="given-name"
            />
            {errors.firstName && <div className="form-error">{errors.firstName}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">Last Name</label>
            <input type="text" id="lastName" name="lastName"
              className={`form-input ${errors.lastName ? 'error' : ''}`}
              placeholder="Doe"
              value={formData.lastName}
              onChange={handleChange}
              autoComplete="family-name"
            />
            {errors.lastName && <div className="form-error">{errors.lastName}</div>}
          </div>
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">Email</label>
          <input type="email" id="email" name="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="john@example.com"
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
          />
          {errors.email && <div className="form-error">{errors.email}</div>}
        </div>

        {/* Mobile */}
        <div className="form-group">
          <label htmlFor="mobileNumber" className="form-label">Mobile Number</label>
          <input type="tel" id="mobileNumber" name="mobileNumber"
            className={`form-input ${errors.mobileNumber ? 'error' : ''}`}
            placeholder="8888888888"
            maxLength={10}
            value={formData.mobileNumber}
            onChange={handleChange}
            autoComplete="tel"
          />
          {errors.mobileNumber && <div className="form-error">{errors.mobileNumber}</div>}
        </div>

        {/* Password */}
        <div className="form-group">
          <label htmlFor="password" className="form-label">Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showPassword ? 'text' : 'password'} id="password" name="password"
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
              }}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              )}
            </button>
          </div>
          {errors.password && <div className="form-error">{errors.password}</div>}
          {formData.password && <PasswordStrengthIndicator strength={passwordStrength} />}
        </div>

        {/* Confirm Password */}
        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword"
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
              }}
            >
              {showConfirmPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              )}
            </button>
          </div>
          {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
        </div>

        {/* Terms & Conditions */}
        <div className="">
          <div className="remember-me-option">
            <input type="checkbox" id="agreeTerms" name="agreeTerms"
              checked={formData.agreeTerms}
              onChange={handleChange}
              className={`remember-me-checkbox ${errors.agreeTerms ? 'error' : ''}`}
            />
            <label htmlFor="agreeTerms" className="remember-me-label ">
              <span style={{ fontSize: '0.875rem' }}>
                I agree to the Terms of Service and Privacy Policy
              </span>
            </label>
          </div>
          {errors.agreeTerms && <div className="form-error">{errors.agreeTerms}</div>}
        </div>

        {/* Submit Button */}
        <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}
        >
          
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
    );
  }

  // Full standalone registration page
  if (isRegistrationComplete) {
    return (
      <AuthPageShell
        title="Check Your Email"
        subtitle={<>We sent a verification link to <strong>{formData.email}</strong></>}
        bodyStyle={{ textAlign: 'center' }}
      >
        <p className="auth-muted" style={{ lineHeight: 1.6, marginBottom: 24 }}>
          Click the link in your email to verify your account. Once verified, you can sign in and get started.
        </p>
        <ResendVerificationEmail
          defaultEmail={formData.email}
          showEmailInput={false}
          readOnlyEmail
          variant="link"
          className="mb-4"
        />
        <AnimatedButton
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => navigate('/auth/login')}
        >
          Go to Login
        </AnimatedButton>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell
      title="Create an Account"
      subtitle="Join us today and get started"
      cardStyle={{ overflowY: 'auto', overflowX: 'hidden' }}
      footer={
        <p>
          Already have an account?{' '}
          <button type="button" className="btn-link" onClick={() => navigate('/auth/login')}>
            Sign In
          </button>
        </p>
      }
    >
            {/* Social buttons */}
            <div className="social-buttons">
              {/* Facebook */}
              <AnimatedSocialButton ariaLabel="Register with Facebook" icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              }/>
              {/* Google */}
              <AnimatedSocialButton ariaLabel="Register with Google" icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path d="M21.8 10.04H21V10H12v4h5.65C16.83 16.33 14.61 18 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.53 0 2.92.58 3.98 1.52l2.83-2.83C17.02 3.03 14.63 2 12 2 6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10c0-.67-.07-1.33-.19-1.96z" fill="#FFC107"/>
                </svg>
              }/>
              {/* Apple */}
              <AnimatedSocialButton ariaLabel="Register with Apple" icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              }/>
            </div>

            <div className="divider"><span className="divider-text">or sign in with email</span></div>

            <form onSubmit={handleSubmit}>
              {apiError && <div className="form-error mb-3">{apiError}</div>}

              {/* First Name */}
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input type="text" id="firstName" name="firstName"
                  className={`form-input ${errors.firstName ? 'error' : ''}`}
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                />
                {errors.firstName && <div className="form-error">{errors.firstName}</div>}
              </div>

              {/* Last Name */}
              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input type="text" id="lastName" name="lastName"
                  className={`form-input ${errors.lastName ? 'error' : ''}`}
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
                {errors.lastName && <div className="form-error">{errors.lastName}</div>}
              </div>

              {/* Email */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  className={`form-input ${errors.email ? 'error' : ''} ${tokenData?.email ? 'form-input--readonly' : ''}`}
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  readOnly={!!tokenData?.email}
                  title={tokenData?.email ? "Email is pre-filled from your invitation" : "Enter your email address"}
                />
                {tokenData?.email && (
                  <div className="form-text form-text--success">
                    <span>✓</span>
                    Email verified from invitation
                  </div>
                )}
                {errors.email && <div className="form-error">{errors.email}</div>}
              </div>

              {/* Mobile */}
              <div className="form-group">
                <label htmlFor="mobileNumber" className="form-label">Mobile Number</label>
                <input type="tel" id="mobileNumber" name="mobileNumber"
                  className={`form-input ${errors.mobileNumber ? 'error' : ''}`}
                  placeholder="8888888888"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  maxLength={10}
                  autoComplete="tel"
                  style={{ width: '100%' }}
                />
                {errors.mobileNumber && <div className="form-error">{errors.mobileNumber}</div>}
              </div>

              {/* Password */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="auth-password-field">
                  <input type={showPassword ? 'text' : 'password'} id="password" name="password"
                    className={`form-input ${errors.password ? 'error' : ''}`}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <div className="form-error">{errors.password}</div>}
                {formData.password && <PasswordStrengthIndicator strength={passwordStrength} />}
                <div className="form-text">Password must be at least 8 characters and include uppercase, lowercase, and numbers</div>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                <div className="auth-password-field">
                  <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword"
                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
              </div>

              {/* Agree Terms */}
              <div className="form-group">
                <div className="remember-me-option">
                  <input type="checkbox" id="agreeTerms" name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    className={`remember-me-checkbox ${errors.agreeTerms ? 'error' : ''}`}
                  />
                  <label htmlFor="agreeTerms" className="remember-me-label" title="I agree to the Terms of Service and Privacy Policy">
                    <span className="terms-text">
                      I agree to the <button type="button" className="btn-link" onClick={handleTermsClick}>Terms of Service</button> and <button type="button" className="btn-link" onClick={handlePrivacyClick}>Privacy Policy</button>
                    </span>
                  </label>
                </div>
                {errors.agreeTerms && <div className="form-error">{errors.agreeTerms}</div>}
              </div>

              {/* Submit Button */}
              <AnimatedButton type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </AnimatedButton>
            </form>
    </AuthPageShell>
  );
};

export default Register;
